export const periodStatuses = [
  "active",
  "finished",
  "pending",
  "disabled",
] as const;

export const ObjectivePeriodStatuses = [
  "active",
  "finished",
  "pending",
  "form",
] as const;

export const reviewPeriodTypes = [
  "self",
  "manager",
  "peer",
  "manager_peer",
  "calibration",
] as const;

export const keyResultsTypes = [
  "percentage",
  "number",
  "money",
  "boolean",
] as const;

export const objectiveStatuses = [
  "draft",
  "pending_manager",
  "pending_parent_objective_owner",
  "no_status",
  "doing",
  "completed",
  "paused",
  "cancelled",
  "pending",
  "rejected",
] as const;

export type ObjectiveStatus = (typeof objectiveStatuses)[number];

/** Statuses a user may switch to from the status dropdown. */
export const changeableObjectiveStatuses = [
  "no_status",
  "doing",
  "completed",
  "paused",
] as const;

/** Statuses that mean the objective is still waiting for an approval. */
export const pendingObjectiveStatuses = [
  "pending",
  "pending_manager",
  "pending_parent_objective_owner",
] as const;

export const objectiveStatusColors = {
  draft: "gray",
  pending_manager: "warning",
  pending_parent_objective_owner: "warning",
  no_status: "gray",
  doing: "primary",
  completed: "success",
  paused: "warning",
  cancelled: "error",
  pending: "warning",
  rejected: "error",
} as const satisfies Record<ObjectiveStatus, string>;

export const objectiveTypeAccessRoles = [
  "owner",
  "direct_manager",
  "assistant",
  "user_representative",
  "manager_representative",
  "self",
  "manager",
  "peer",
  "manager_peer",
] as const;

export type ObjectiveTypeAccessRole = (typeof objectiveTypeAccessRoles)[number];

/**
 * Question rows shown in the objective-type access modal.
 * Each role maps to one boolean field on the type.
 */
export const objectiveTypeAccessQuestions = [
  {
    id: "who_can_create",
    kind: "roles",
    roles: [
      { role: "owner", field: "can_create_objectives_by_owner" },
      {
        role: "direct_manager",
        field: "can_create_objectives_by_direct_manager",
      },
      { role: "assistant", field: "can_create_objectives_by_assistant" },
    ],
  },
  {
    id: "create_requires_approval",
    kind: "roles",
    ownerField: "can_create_objectives_by_owner",
    roles: [
      {
        role: "direct_manager",
        field: "requires_create_direct_manager_approval",
      },
      { role: "assistant", field: "requires_create_assistant_approval" },
    ],
  },
  {
    id: "who_can_edit",
    kind: "roles",
    roles: [
      { role: "owner", field: "can_edit_objectives_by_owner" },
      {
        role: "direct_manager",
        field: "can_edit_objectives_by_direct_manager",
      },
      { role: "assistant", field: "can_edit_objectives_by_assistant" },
    ],
  },
  {
    id: "edit_requires_approval",
    kind: "roles",
    ownerField: "can_edit_objectives_by_owner",
    roles: [
      {
        role: "direct_manager",
        field: "requires_edit_direct_manager_approval",
      },
      { role: "assistant", field: "requires_edit_assistant_approval" },
    ],
  },
  {
    id: "who_can_update",
    kind: "roles",
    roles: [
      { role: "owner", field: "can_update_objectives_by_owner" },
      {
        role: "direct_manager",
        field: "can_update_objectives_by_direct_manager",
      },
      { role: "assistant", field: "can_update_objectives_by_assistant" },
    ],
  },
  {
    id: "update_requires_approval",
    kind: "roles",
    ownerField: "can_update_objectives_by_owner",
    roles: [
      {
        role: "direct_manager",
        field: "requires_update_direct_manager_approval",
      },
      { role: "assistant", field: "requires_update_assistant_approval" },
    ],
  },
  {
    id: "can_weight",
    kind: "toggle",
    field: "can_weight",
  },
  {
    id: "who_can_rate",
    kind: "roles",
    roles: [
      { role: "self", field: "can_rate_by_self" },
      { role: "manager", field: "can_rate_by_manager" },
      { role: "peer", field: "can_rate_by_peer" },
      { role: "manager_peer", field: "can_rate_by_manager_peer" },
    ],
  },
] as const;

export const objectiveTypeAccessFields = [
  "can_create_objectives_by_owner",
  "can_create_objectives_by_direct_manager",
  "can_create_objectives_by_assistant",
  "requires_create_direct_manager_approval",
  "requires_create_assistant_approval",
  "can_edit_objectives_by_owner",
  "can_edit_objectives_by_direct_manager",
  "can_edit_objectives_by_assistant",
  "requires_edit_direct_manager_approval",
  "requires_edit_assistant_approval",
  "can_update_objectives_by_owner",
  "can_update_objectives_by_direct_manager",
  "can_update_objectives_by_assistant",
  "requires_update_direct_manager_approval",
  "requires_update_assistant_approval",
  "can_weight",
  "can_rate_by_self",
  "can_rate_by_manager",
  "can_rate_by_peer",
  "can_rate_by_manager_peer",
] as const;

export type ObjectiveTypeAccessField =
  (typeof objectiveTypeAccessFields)[number];

export type ObjectiveTypeAccess = Record<ObjectiveTypeAccessField, boolean>;

export const objectiveStatusTextColors = {
  draft: "text-gray-600",
  pending_manager: "text-warning-700",
  pending_parent_objective_owner: "text-warning-700",
  no_status: "text-gray-600",
  doing: "text-blue-700",
  completed: "text-success-700",
  paused: "text-gray-700",
  cancelled: "text-error-700",
  pending: "text-warning-700",
  rejected: "text-error-700",
} as const satisfies Record<ObjectiveStatus, string>;

export const getObjectiveProgressBarColor = (status: ObjectiveStatus) => {
  if (status === "completed") return "bg-success-500";
  if (status === "doing") return "bg-blue-500";
  return "bg-gray-500";
};

export const objectiveActionTypes = [
  "delete",
  "cancel",
  "cancel_request",
  "withdraw_cancel_request",
  "restore_request",
  "restore",
  "none",
] as const;

export type ObjectiveActionType = (typeof objectiveActionTypes)[number];

export const objectiveRequestTypes = [
  "create_requested",
  "edit_requested",
  "update_requested",
  "cancel_requested",
  "restore_requested",
] as const;

export type ObjectiveRequestType = (typeof objectiveRequestTypes)[number];
